import React, {useState, useCallback} from 'react';
import {View, StyleSheet, ScrollView, Platform, TouchableOpacity} from 'react-native';
import {
  Appbar,
  TextInput,
  Button,
  SegmentedButtons,
  Switch,
  RadioButton,
  Text,
  Snackbar,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import DateTimePicker from '@react-native-community/datetimepicker';
import {useCouponStore} from '../../store/useCouponStore';
import {colors, spacing} from '../../theme';

const CouponFormScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const existing = route.params?.coupon;

  const {createCoupon, updateCoupon} = useCouponStore();

  const [type, setType] = useState(existing?.type || 'PERCENTAGE');
  const [value, setValue] = useState(existing ? String(Number(existing.value)) : '');
  const [maxDiscountValue, setMaxDiscountValue] = useState(
    existing?.maxDiscountValue ? String(Number(existing.maxDiscountValue)) : '',
  );
  const [name, setName] = useState(existing?.name || '');
  const [code, setCode] = useState(existing?.code || '');
  const [minOrderValue, setMinOrderValue] = useState(
    existing?.minOrderValue ? String(Number(existing.minOrderValue)) : '',
  );
  const [expiryDate, setExpiryDate] = useState(
    existing?.expiryDate ? new Date(existing.expiryDate) : null,
  );
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isPublic, setIsPublic] = useState(
    existing ? existing.availability === 'PUBLIC' : true,
  );
  const [applicability, setApplicability] = useState(
    existing?.applicability || 'UNLIMITED',
  );
  const [usageLimit, setUsageLimit] = useState(
    existing?.usageLimit ? String(existing.usageLimit) : '',
  );
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  const handleDateChange = useCallback((event, selectedDate) => {
    setShowDatePicker(Platform.OS === 'ios');
    if (selectedDate) {
      setExpiryDate(selectedDate);
    }
  }, []);

  const formatDate = useCallback(date => {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, []);

  const handleSave = useCallback(async () => {
    const trimmedName = name.trim();
    const trimmedCode = code.trim();
    if (!trimmedName || !trimmedCode || !value) return;

    const payload = {
      name: trimmedName,
      code: trimmedCode,
      type,
      value: parseFloat(value),
      minOrderValue: minOrderValue ? parseFloat(minOrderValue) : null,
      maxDiscountValue:
        type === 'PERCENTAGE' && maxDiscountValue
          ? parseFloat(maxDiscountValue)
          : null,
      expiryDate: expiryDate ? formatDate(expiryDate) : null,
      availability: isPublic ? 'PUBLIC' : 'PRIVATE',
      applicability,
      usageLimit:
        applicability === 'LIMITED' && usageLimit
          ? parseInt(usageLimit, 10)
          : null,
    };

    setSaving(true);
    try {
      if (existing) {
        await updateCoupon(existing.id, payload);
      } else {
        await createCoupon(payload);
      }
      navigation.goBack();
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  }, [
    name,
    code,
    value,
    type,
    minOrderValue,
    maxDiscountValue,
    expiryDate,
    isPublic,
    applicability,
    usageLimit,
    existing,
    createCoupon,
    updateCoupon,
    navigation,
    formatDate,
    t,
  ]);

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction
          onPress={() => navigation.goBack()}
          color={colors.white}
        />
        <Appbar.Content
          title={existing ? t('coupon.editOffer') : t('coupon.newOffer')}
          titleStyle={styles.appbarTitle}
        />
      </Appbar.Header>

      <ScrollView contentContainerStyle={styles.form}>
        {/* Coupon Type */}
        <Text style={styles.label}>{t('coupon.type')}</Text>
        <SegmentedButtons
          value={type}
          onValueChange={setType}
          buttons={[
            {value: 'PERCENTAGE', label: t('coupon.percentage')},
            {value: 'FIXED_AMOUNT', label: t('coupon.fixedAmount')},
          ]}
          style={styles.segmented}
        />

        {/* Value */}
        <TextInput
          label={
            type === 'PERCENTAGE'
              ? t('coupon.percentageValue')
              : t('coupon.fixedValue')
          }
          value={value}
          onChangeText={setValue}
          mode="outlined"
          keyboardType="numeric"
          style={styles.input}
        />

        {/* Max Discount (only for percentage) */}
        {type === 'PERCENTAGE' && (
          <TextInput
            label={t('coupon.maxDiscount')}
            value={maxDiscountValue}
            onChangeText={setMaxDiscountValue}
            mode="outlined"
            keyboardType="numeric"
            style={styles.input}
          />
        )}

        {/* Name */}
        <TextInput
          label={t('coupon.offerName')}
          value={name}
          onChangeText={setName}
          mode="outlined"
          style={styles.input}
        />

        {/* Code */}
        <TextInput
          label={t('coupon.offerCode')}
          value={code}
          onChangeText={text => setCode(text.toUpperCase())}
          mode="outlined"
          autoCapitalize="characters"
          style={styles.input}
        />

        {/* Min Order Value */}
        <TextInput
          label={t('coupon.minOrder')}
          value={minOrderValue}
          onChangeText={setMinOrderValue}
          mode="outlined"
          keyboardType="numeric"
          style={styles.input}
        />

        {/* Expiry Date */}
        <TouchableOpacity activeOpacity={1} onPress={() => setShowDatePicker(true)}>
          <TextInput
            label={t('coupon.expiryDate')}
            value={expiryDate ? formatDate(expiryDate) : ''}
            mode="outlined"
            style={styles.input}
            right={
              <TextInput.Icon
                icon="calendar"
                onPress={() => setShowDatePicker(true)}
              />
            }
            editable={false}
          />
        </TouchableOpacity>
        {showDatePicker && (
          <DateTimePicker
            value={expiryDate || new Date()}
            mode="date"
            display="default"
            minimumDate={new Date()}
            onChange={handleDateChange}
          />
        )}

        {/* Is Public */}
        <View style={styles.switchRow}>
          <Text style={styles.switchLabel}>{t('coupon.isPublic')}</Text>
          <Switch value={isPublic} onValueChange={setIsPublic} />
        </View>

        {/* Applicability */}
        <Text style={styles.label}>{t('coupon.promoUsage')}</Text>
        <RadioButton.Group
          value={applicability}
          onValueChange={setApplicability}>
          <RadioButton.Item
            label={t('coupon.firstOrder')}
            value="FIRST_ORDER"
          />
          <RadioButton.Item
            label={t('coupon.oncePerCustomer')}
            value="ONCE_PER_CUSTOMER"
          />
          <RadioButton.Item
            label={t('coupon.unlimited')}
            value="UNLIMITED"
          />
          <RadioButton.Item
            label={t('coupon.limited')}
            value="LIMITED"
          />
        </RadioButton.Group>

        {/* Usage Limit (only for LIMITED) */}
        {applicability === 'LIMITED' && (
          <TextInput
            label={t('coupon.usageLimit')}
            value={usageLimit}
            onChangeText={setUsageLimit}
            mode="outlined"
            keyboardType="numeric"
            style={styles.input}
          />
        )}

        <Button
          mode="contained"
          onPress={handleSave}
          loading={saving}
          disabled={saving}
          style={styles.saveButton}>
          {t('common.save')}
        </Button>
      </ScrollView>

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  form: {
    padding: spacing.md,
    paddingBottom: spacing.xxl,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text,
    marginBottom: spacing.xs,
    marginTop: spacing.sm,
  },
  segmented: {
    marginBottom: spacing.sm,
  },
  input: {
    marginBottom: spacing.sm,
    backgroundColor: colors.surface,
  },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.xs,
  },
  switchLabel: {
    fontSize: 16,
    color: colors.text,
  },
  saveButton: {
    marginTop: spacing.lg,
    backgroundColor: colors.primary,
    paddingVertical: spacing.xs,
  },
});

export default CouponFormScreen;
